import React, { useState } from 'react';

// ==================== TYPES ====================
export enum RestructureReason {
	SALARY_REDUCTION = 'SALARY_REDUCTION',
	JOB_LOSS = 'JOB_LOSS',
	EARLY_RETIREMENT = 'EARLY_RETIREMENT',
	MEDICAL_DISABILITY = 'MEDICAL_DISABILITY',
	VOLUNTARY_RELIEF = 'VOLUNTARY_RELIEF',
	CURE_DEFAULT = 'CURE_DEFAULT'
}

interface FinancialParameters {
	financeAmount: number;
	equatedMonthlyInstallment: number;
	tenure: number;
	minimumDownPayment: number;
	minimumDownPaymentPercent: number;
	annualProfitRate: number;
	propertyPrice: number;
}

interface DecisionStep {
	step: string;
	description: string;
	result: any;
	passed: boolean;
}

interface OriginalData {
	baseIncome?: number;
	baseIncomeSource?: string;
	additionalIncome?: number;
	obligations?: number;
	dbr?: number;
}

interface NewData {
	baseIncome?: number;
	additionalIncome?: number;
	obligations?: number;
	dbr?: number;
	pensionAmount?: number;
}

interface Thresholds {
	maxDbr?: number;
	racMaxDbr?: number;
}

interface RestructureMetadata {
	restructureType: string;
	reason: string;
	decisionSteps: DecisionStep[];
	dataSource?: string;
	originalData?: OriginalData;
	newData?: NewData;
	thresholds?: Thresholds;
	timestamp: string;
}

interface RestructureResponseData {
	restructurAccepted: boolean;
	financialParameters?: FinancialParameters;
	metadata: RestructureMetadata;
}

interface ApiError {
	code: string;
	message: string;
}

interface ApiResponse {
	success: boolean;
	data?: RestructureResponseData;
	message?: string;
	errors?: ApiError[];
}

// ==================== CONSTANTS ====================
const RESTRUCTURE_REASON_LABELS: Record<RestructureReason, string> = {
	[RestructureReason.SALARY_REDUCTION]: 'Salary Reduction',
	[RestructureReason.JOB_LOSS]: 'Job Loss',
	[RestructureReason.EARLY_RETIREMENT]: 'Early Retirement',
	[RestructureReason.MEDICAL_DISABILITY]: 'Medical Disability',
	[RestructureReason.VOLUNTARY_RELIEF]: 'Voluntary Relief',
	[RestructureReason.CURE_DEFAULT]: 'Cure Default'
};

const RESTRUCTURE_REASON_DESCRIPTIONS: Record<RestructureReason, string> = {
	[RestructureReason.SALARY_REDUCTION]: 'Request restructure due to a reduction in your salary income',
	[RestructureReason.JOB_LOSS]: 'Request restructure due to loss of employment',
	[RestructureReason.EARLY_RETIREMENT]: 'Request restructure due to early retirement with pension',
	[RestructureReason.MEDICAL_DISABILITY]: 'Request restructure due to income reduction from medical disability',
	[RestructureReason.VOLUNTARY_RELIEF]: 'Request restructure to improve debt-to-income ratio after obligation reduction',
	[RestructureReason.CURE_DEFAULT]: 'Request restructure to cure payment defaults shown in credit history'
};

const RESTRUCTURE_REASON_ICONS: Record<RestructureReason, string> = {
	[RestructureReason.SALARY_REDUCTION]: '💰',
	[RestructureReason.JOB_LOSS]: '👤',
	[RestructureReason.EARLY_RETIREMENT]: '🎯',
	[RestructureReason.MEDICAL_DISABILITY]: '🏥',
	[RestructureReason.VOLUNTARY_RELIEF]: '📊',
	[RestructureReason.CURE_DEFAULT]: '✓'
};

// ==================== UTILITY FUNCTIONS ====================
const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat('en-SA', {
		style: 'currency',
		currency: 'SAR',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}).format(amount);
};

const formatTimestamp = (iso: string): string => {
	const date = new Date(iso);
	return date.toLocaleString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		timeZoneName: 'short'
	});
};

const formatPercentage = (value: number): string => {
	return `${(value * 100).toFixed(2)}%`;
};

const formatTenure = (months: number): string => {
	const years = Math.floor(months / 12);
	const remainingMonths = months % 12;
	if (years === 0) return `${months} months`;
	if (remainingMonths === 0) return `${years} year${years > 1 ? 's' : ''}`;
	return `${months} months (${years} year${years > 1 ? 's' : ''})`;
};

const formatStageName = (name: string): string =>
	name
		.split('_')
		.map((w) => w.charAt(0) + w.slice(1).toLowerCase())
		.join(' ');

// ==================== SUB-COMPONENTS ====================

const DecisionSummaryCard: React.FC<{ data: RestructureResponseData }> = ({ data }) => {
	const accepted = data.restructurAccepted;
	const reasonLabel = RESTRUCTURE_REASON_LABELS[data.metadata.reason as RestructureReason];

	return (
		<div
			className={`rounded-lg border-2 p-6 ${
				accepted ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'
			}`}>
			<div className="flex items-start justify-between">
				<div>
					<div className="flex items-center gap-3 mb-2">
						<span className="text-4xl">{accepted ? '✅' : '❌'}</span>
						<div>
							<h3 className="text-2xl font-bold text-gray-900">
								{accepted ? 'Restructure Request Accepted' : 'Restructure Request Rejected'}
							</h3>
							<p className="text-sm text-gray-600 mt-1">
								{accepted
									? `Your restructure request has been approved based on ${reasonLabel}`
									: 'Your restructure request was not approved at this time'}
							</p>
						</div>
					</div>
					<p className="text-sm text-gray-500 ml-[52px]">Decision made on: {formatTimestamp(data.metadata.timestamp)}</p>
				</div>
			</div>
		</div>
	);
};

const FinancialParametersSection: React.FC<{ params: FinancialParameters }> = ({ params }) => (
	<div className="bg-white p-6 rounded-lg shadow-sm border">
		<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">New Loan Terms</h3>
		<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
			<div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
				<div className="text-sm text-gray-600 mb-1">Finance Amount</div>
				<div className="text-2xl font-bold text-blue-700">{formatCurrency(params.financeAmount)}</div>
			</div>
			<div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
				<div className="text-sm text-gray-600 mb-1">Monthly Payment (EMI)</div>
				<div className="text-2xl font-bold text-purple-700">{formatCurrency(params.equatedMonthlyInstallment)}</div>
			</div>
			<div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
				<div className="text-sm text-gray-600 mb-1">Loan Tenure</div>
				<div className="text-2xl font-bold text-orange-700">{formatTenure(params.tenure)}</div>
			</div>
			<div className="bg-green-50 p-4 rounded-lg border border-green-200">
				<div className="text-sm text-gray-600 mb-1">Annual Profit Rate</div>
				<div className="text-2xl font-bold text-green-700">{params.annualProfitRate}%</div>
			</div>
			<div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
				<div className="text-sm text-gray-600 mb-1">Property Price</div>
				<div className="text-2xl font-bold text-gray-700">{formatCurrency(params.propertyPrice)}</div>
			</div>
			<div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
				<div className="text-sm text-gray-600 mb-1">Down Payment Required</div>
				<div className="text-2xl font-bold text-yellow-700">
					{formatCurrency(params.minimumDownPayment)}
					<span className="text-sm font-normal text-gray-600 ml-2">({params.minimumDownPaymentPercent}%)</span>
				</div>
			</div>
		</div>
	</div>
);

const MetadataOverviewPanel: React.FC<{ metadata: RestructureMetadata }> = ({ metadata }) => {
	const passedSteps = metadata.decisionSteps.filter((s) => s.passed).length;
	const totalSteps = metadata.decisionSteps.length;

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Decision Details</h3>
			<div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
				<div>
					<div className="text-xs text-gray-500 mb-1">Restructure Type</div>
					<div className="font-semibold text-gray-800">
						{RESTRUCTURE_REASON_ICONS[metadata.reason as RestructureReason]}{' '}
						{RESTRUCTURE_REASON_LABELS[metadata.reason as RestructureReason]}
					</div>
				</div>
				{metadata.dataSource && (
					<div>
						<div className="text-xs text-gray-500 mb-1">Data Source</div>
						<div className="font-semibold text-gray-800">{metadata.dataSource}</div>
					</div>
				)}
				<div>
					<div className="text-xs text-gray-500 mb-1">Decision Made</div>
					<div className="font-semibold text-gray-800">{formatTimestamp(metadata.timestamp)}</div>
				</div>
				<div>
					<div className="text-xs text-gray-500 mb-1">Total Steps</div>
					<div className="font-semibold text-gray-800">{totalSteps} steps</div>
				</div>
				<div>
					<div className="text-xs text-gray-500 mb-1">Steps Passed</div>
					<div className={`font-semibold ${passedSteps === totalSteps ? 'text-green-600' : 'text-orange-600'}`}>
						{passedSteps} of {totalSteps}
					</div>
				</div>
			</div>
		</div>
	);
};

const DataComparisonTable: React.FC<{ originalData?: OriginalData; newData?: NewData }> = ({
	originalData,
	newData
}) => {
	if (!originalData && !newData) return null;

	const calculateChange = (original?: number, newVal?: number) => {
		if (original === undefined || newVal === undefined) return null;
		if (original === 0) return null;
		const change = ((newVal - original) / original) * 100;
		return change;
	};

	const renderChange = (change: number | null) => {
		if (change === null) return <span className="text-gray-400">—</span>;
		const isPositive = change > 0;
		const isNegative = change < 0;
		return (
			<span className={isPositive ? 'text-red-600' : isNegative ? 'text-green-600' : 'text-gray-600'}>
				{isPositive ? '↑' : isNegative ? '↓' : '—'} {Math.abs(change).toFixed(1)}%
			</span>
		);
	};

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Financial Comparison</h3>
			<div className="overflow-x-auto">
				<table className="min-w-full text-sm">
					<thead>
						<tr className="border-b-2 border-gray-300">
							<th className="text-left py-3 pr-4 font-semibold text-gray-700">Metric</th>
							<th className="text-right py-3 px-4 font-semibold text-gray-700">Original</th>
							<th className="text-right py-3 px-4 font-semibold text-gray-700">New</th>
							<th className="text-right py-3 pl-4 font-semibold text-gray-700">Change</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-200">
						<tr className="hover:bg-gray-50">
							<td className="py-3 pr-4 text-gray-700">Base Income</td>
							<td className="text-right py-3 px-4 font-medium">
								{originalData?.baseIncome ? formatCurrency(originalData.baseIncome) : '—'}
							</td>
							<td className="text-right py-3 px-4 font-medium">
								{newData?.baseIncome ? formatCurrency(newData.baseIncome) : '—'}
							</td>
							<td className="text-right py-3 pl-4 font-semibold">
								{renderChange(calculateChange(originalData?.baseIncome, newData?.baseIncome))}
							</td>
						</tr>
						<tr className="hover:bg-gray-50">
							<td className="py-3 pr-4 text-gray-700">Additional Income</td>
							<td className="text-right py-3 px-4 font-medium">
								{originalData?.additionalIncome ? formatCurrency(originalData.additionalIncome) : '—'}
							</td>
							<td className="text-right py-3 px-4 font-medium">
								{newData?.additionalIncome ? formatCurrency(newData.additionalIncome) : '—'}
							</td>
							<td className="text-right py-3 pl-4 font-semibold">
								{renderChange(calculateChange(originalData?.additionalIncome, newData?.additionalIncome))}
							</td>
						</tr>
						<tr className="hover:bg-gray-50">
							<td className="py-3 pr-4 text-gray-700">Monthly Obligations</td>
							<td className="text-right py-3 px-4 font-medium">
								{originalData?.obligations ? formatCurrency(originalData.obligations) : '—'}
							</td>
							<td className="text-right py-3 px-4 font-medium">
								{newData?.obligations ? formatCurrency(newData.obligations) : '—'}
							</td>
							<td className="text-right py-3 pl-4 font-semibold">
								{renderChange(calculateChange(originalData?.obligations, newData?.obligations))}
							</td>
						</tr>
						<tr className="hover:bg-gray-50">
							<td className="py-3 pr-4 text-gray-700">Debt-to-Income Ratio</td>
							<td className="text-right py-3 px-4 font-medium">
								{originalData?.dbr !== undefined ? formatPercentage(originalData.dbr) : '—'}
							</td>
							<td className="text-right py-3 px-4 font-medium">
								{newData?.dbr !== undefined ? formatPercentage(newData.dbr) : '—'}
							</td>
							<td className="text-right py-3 pl-4 font-semibold">
								{renderChange(calculateChange(originalData?.dbr ? originalData.dbr * 100 : undefined, newData?.dbr ? newData.dbr * 100 : undefined))}
							</td>
						</tr>
						{newData?.pensionAmount !== undefined && (
							<tr className="hover:bg-gray-50 bg-blue-50">
								<td className="py-3 pr-4 text-gray-700 font-medium">Pension Amount</td>
								<td className="text-right py-3 px-4 font-medium text-gray-400">—</td>
								<td className="text-right py-3 px-4 font-medium text-blue-700">
									{formatCurrency(newData.pensionAmount)}
								</td>
								<td className="text-right py-3 pl-4 font-semibold text-blue-600">New</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
};

const ThresholdsPanel: React.FC<{ thresholds: Thresholds }> = ({ thresholds }) => (
	<div className="bg-white p-6 rounded-lg shadow-sm border">
		<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Decision Thresholds</h3>
		<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
			{thresholds.maxDbr !== undefined && (
				<div className="bg-gray-50 p-4 rounded-lg border">
					<div className="text-sm text-gray-600 mb-1">Maximum DBR</div>
					<div className="text-2xl font-bold text-gray-800">{formatPercentage(thresholds.maxDbr)}</div>
				</div>
			)}
			{thresholds.racMaxDbr !== undefined && (
				<div className="bg-gray-50 p-4 rounded-lg border">
					<div className="text-sm text-gray-600 mb-1">RAC Maximum DBR</div>
					<div className="text-2xl font-bold text-gray-800">{formatPercentage(thresholds.racMaxDbr)}</div>
				</div>
			)}
		</div>
	</div>
);

const renderResultValue = (key: string, value: any): React.ReactElement => {
	// Format numbers
	if (typeof value === 'number') {
		// Check if it's a DBR/rate field
		if (key.toLowerCase().includes('dbr') || key.toLowerCase().includes('rate') || key.toLowerCase().includes('percentage')) {
			if (value < 1) {
				return <span className="font-semibold text-purple-700">{formatPercentage(value)}</span>;
			}
			return <span className="font-semibold text-purple-700">{value.toFixed(2)}%</span>;
		}
		// Check if it's a currency field
		if (
			key.toLowerCase().includes('income') ||
			key.toLowerCase().includes('amount') ||
			key.toLowerCase().includes('payment') ||
			key.toLowerCase().includes('obligation') ||
			key.toLowerCase().includes('pension')
		) {
			return <span className="font-semibold text-green-700">{formatCurrency(value)}</span>;
		}
		return <span className="font-semibold text-gray-800">{value.toLocaleString()}</span>;
	}

	// Format booleans
	if (typeof value === 'boolean') {
		return (
			<span className={`px-2 py-0.5 rounded text-xs font-bold ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
				{value ? 'Yes' : 'No'}
			</span>
		);
	}

	// Format arrays
	if (Array.isArray(value)) {
		return (
			<ul className="list-disc list-inside space-y-1 text-sm">
				{value.map((item, idx) => (
					<li key={idx} className="text-gray-700">
						{typeof item === 'object' ? JSON.stringify(item) : String(item)}
					</li>
				))}
			</ul>
		);
	}

	// Format nested objects
	if (typeof value === 'object' && value !== null) {
		return (
			<div className="pl-4 space-y-1 text-sm">
				{Object.entries(value).map(([nestedKey, nestedValue]) => (
					<div key={nestedKey} className="flex gap-2">
						<span className="text-gray-500">{formatStageName(nestedKey)}:</span>
						{renderResultValue(nestedKey, nestedValue)}
					</div>
				))}
			</div>
		);
	}

	// Default: render as string
	return <span className="text-gray-800">{value === null || value === undefined ? '—' : String(value)}</span>;
};

const DecisionTrail: React.FC<{ steps: DecisionStep[] }> = ({ steps }) => (
	<div className="bg-white p-6 rounded-lg shadow-sm border">
		<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Decision Trail (Step-by-Step Process)</h3>
		<div className="space-y-3">
			{steps.map((step, index) => (
				<div
					key={index}
					className={`border-2 rounded-lg p-4 ${
						step.passed ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'
					}`}>
					<div className="flex items-start justify-between mb-2">
						<div className="flex items-start gap-3 flex-1">
							<span className="text-2xl flex-shrink-0">{step.passed ? '✅' : '❌'}</span>
							<div className="flex-1">
								<div className="flex items-center gap-2 mb-1">
									<span className="text-sm font-semibold text-gray-500">Step {index + 1}:</span>
									<span className="text-base font-bold text-gray-900">{formatStageName(step.step)}</span>
								</div>
								<p className="text-sm text-gray-600">{step.description}</p>
							</div>
						</div>
						<span
							className={`px-3 py-1 rounded-full text-xs font-bold flex-shrink-0 ${
								step.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
							}`}>
							{step.passed ? 'PASSED' : 'FAILED'}
						</span>
					</div>

					{/* Results */}
					{step.result && Object.keys(step.result).length > 0 && (
						<div className="mt-3 bg-white rounded-lg p-3 border">
							<div className="text-xs font-semibold text-gray-600 mb-2">📊 Results:</div>
							<div className="space-y-2">
								{Object.entries(step.result).map(([key, value]) => (
									<div key={key} className="flex flex-col gap-1">
										<div className="flex items-start gap-2">
											<span className="text-sm text-gray-600 min-w-[150px]">• {formatStageName(key)}:</span>
											<div className="flex-1">{renderResultValue(key, value)}</div>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			))}
		</div>
	</div>
);

const RestructureResults: React.FC<{ data: RestructureResponseData }> = ({ data }) => {
	const [showRawJson, setShowRawJson] = useState(false);

	return (
		<div className="space-y-6">
			{/* Decision Summary */}
			<DecisionSummaryCard data={data} />

			{/* Financial Parameters (if accepted) */}
			{data.financialParameters && <FinancialParametersSection params={data.financialParameters} />}

			{/* Metadata Overview */}
			<MetadataOverviewPanel metadata={data.metadata} />

			{/* Data Comparison */}
			<DataComparisonTable originalData={data.metadata.originalData} newData={data.metadata.newData} />

			{/* Thresholds */}
			{data.metadata.thresholds && <ThresholdsPanel thresholds={data.metadata.thresholds} />}

			{/* Decision Trail */}
			<DecisionTrail steps={data.metadata.decisionSteps} />

			{/* Raw JSON Viewer */}
			<div className="bg-white rounded-lg border shadow-sm">
				<button
					onClick={() => setShowRawJson(!showRawJson)}
					className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
					<span>Raw JSON Response</span>
					<span className="text-gray-400">{showRawJson ? '▲' : '▼'}</span>
				</button>
				{showRawJson && (
					<div className="px-4 pb-4">
						<pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-auto max-h-96 font-mono">
							{JSON.stringify({ success: true, data }, null, 2)}
						</pre>
					</div>
				)}
			</div>
		</div>
	);
};

const ErrorDisplay: React.FC<{ message: string; errors?: ApiError[]; statusCode?: number }> = ({
	message,
	errors,
	statusCode
}) => (
	<div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
		<div className="flex items-start gap-3">
			<span className="text-red-500 text-2xl">⚠️</span>
			<div className="flex-1">
				<h3 className="text-lg font-semibold text-red-800">
					{statusCode === 404 ? 'Application Not Found' : 'Error'}
					{statusCode && <span className="ml-2 text-sm font-normal text-red-600">({statusCode})</span>}
				</h3>
				<p className="text-sm text-red-700 mt-1">{message}</p>
				{errors && errors.length > 0 && (
					<div className="mt-3 space-y-2">
						{errors.map((err, i) => (
							<div key={i} className="bg-red-100 rounded p-2">
								<p className="text-xs font-mono text-red-600">Code: {err.code}</p>
								<p className="text-sm text-red-800">{err.message}</p>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	</div>
);

// ==================== MAIN COMPONENT ====================
interface RestructureRequestProps {
	baseUrl: string;
}

const RestructureRequest: React.FC<RestructureRequestProps> = ({ baseUrl }) => {
	// Form state
	const [applicationId, setApplicationId] = useState('');
	const [restructureReason, setRestructureReason] = useState<RestructureReason | null>(null);

	// Request state
	const [isLoading, setIsLoading] = useState(false);
	const [response, setResponse] = useState<ApiResponse | null>(null);
	const [httpStatus, setHttpStatus] = useState<number | null>(null);
	const [error, setError] = useState('');

	const isValidUUID = (id: string): boolean => {
		return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
	};

	const handleSubmit = async () => {
		// Validation
		if (!applicationId.trim()) {
			setError('Application ID is required');
			return;
		}
		if (!isValidUUID(applicationId.trim())) {
			setError('Application ID must be a valid UUID');
			return;
		}
		if (!restructureReason) {
			setError('Restructure reason is required');
			return;
		}

		setIsLoading(true);
		setResponse(null);
		setHttpStatus(null);
		setError('');

		try {
			const res = await fetch(`${baseUrl}/api/v1/application/restructure`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					applicationId: applicationId.trim(),
					restructureReason: restructureReason
				})
			});

			setHttpStatus(res.status);
			const data: ApiResponse = await res.json();
			setResponse(data);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'An unexpected error occurred while calling the API');
		} finally {
			setIsLoading(false);
		}
	};

	const handleReset = () => {
		setApplicationId('');
		setRestructureReason(null);
		setResponse(null);
		setHttpStatus(null);
		setError('');
	};

	return (
		<div className="space-y-6">
			{/* Request Form */}
			<div className="bg-white p-6 rounded-lg shadow-sm border">
				<div className="flex items-center justify-between mb-4">
					<h3 className="text-lg font-semibold text-gray-800">Restructure Loan Request</h3>
					<span className="text-xs font-mono bg-blue-100 text-blue-700 px-2 py-1 rounded">
						POST /api/v1/application/restructure
					</span>
				</div>

				<p className="text-sm text-gray-600 mb-6">
					Request loan restructuring for an existing application due to changes in financial circumstances.
				</p>

				{/* Form Fields */}
				<div className="space-y-4">
					{/* Application ID Input */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Loan Application ID <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							value={applicationId}
							onChange={(e) => setApplicationId(e.target.value)}
							placeholder="Enter application ID"
							disabled={isLoading}
							className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm font-mono"
						/>
					</div>

					{/* Restructure Reason Selector */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Reason for Restructure <span className="text-red-500">*</span>
						</label>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
							{Object.values(RestructureReason).map((reason) => (
								<button
									key={reason}
									onClick={() => setRestructureReason(reason)}
									disabled={isLoading}
									className={`text-left px-4 py-3 rounded-lg border-2 transition-all ${
										restructureReason === reason
											? 'border-blue-500 bg-blue-50'
											: 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
									} disabled:opacity-50`}>
									<div className="flex items-start gap-3">
										<span className="text-2xl">{RESTRUCTURE_REASON_ICONS[reason]}</span>
										<div className="flex-1">
											<div className="font-semibold text-gray-900 mb-1">{RESTRUCTURE_REASON_LABELS[reason]}</div>
											<div className="text-xs text-gray-600">{RESTRUCTURE_REASON_DESCRIPTIONS[reason]}</div>
										</div>
									</div>
								</button>
							))}
						</div>
					</div>

					{/* Action Buttons */}
					<div className="flex items-center gap-3 pt-2">
						<button
							onClick={handleSubmit}
							disabled={isLoading || !applicationId.trim() || !restructureReason}
							className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm flex items-center gap-2">
							{isLoading ? (
								<>
									<svg
										className="animate-spin h-4 w-4"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24">
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										/>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
										/>
									</svg>
									Submitting...
								</>
							) : (
								'Submit Restructure Request'
							)}
						</button>
						<button
							onClick={handleReset}
							disabled={isLoading}
							className="px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors text-sm">
							Reset
						</button>
					</div>
				</div>
			</div>

			{/* Validation Error */}
			{error && (
				<div className="bg-red-50 border border-red-200 rounded-lg p-4">
					<p className="text-sm text-red-700">{error}</p>
				</div>
			)}

			{/* API Response */}
			{response && (
				<div className="space-y-4">
					{/* HTTP Status Indicator */}
					<div className="flex items-center gap-2">
						<span
							className={`text-xs font-mono px-2 py-1 rounded ${
								httpStatus === 200
									? 'bg-green-100 text-green-700'
									: httpStatus === 404
									? 'bg-yellow-100 text-yellow-700'
									: 'bg-red-100 text-red-700'
							}`}>
							HTTP {httpStatus}
						</span>
						<span className="text-xs text-gray-400">|</span>
						<span className="text-xs text-gray-500">Response received</span>
					</div>

					{response.success && response.data ? (
						<RestructureResults data={response.data} />
					) : (
						<ErrorDisplay message={response.message || 'Unknown error'} errors={response.errors} statusCode={httpStatus || undefined} />
					)}
				</div>
			)}
		</div>
	);
};

export default RestructureRequest;
